import React, { useEffect, useRef, useCallback, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { message, Popover } from 'antd'
import { debounce } from 'lodash'

import Quill from 'quill'
import 'quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import { Mention } from 'quill-mention'
import AvatarMentionBlot, {
    getMentionInitials,
    getMentionAvatarColor,
    buildMentionPillElement,
    resolveProfilePictureUrl,
} from './MentionEditorMentionBlot'
import dayjs from 'dayjs'

// css
import './index.css'
import insertMenuStyles from './MentionEditorInsertMenu.module.css'
import assigneeStyles from '../../modules/projects/components/AssigneeSelector.module.css'

// components
import Button from '../button/button'
import MentionEditorInsertMenu from './MentionEditorInsertMenu'
import {
    resolveAppLinkTitle,
    getLinkHref,
} from '../../modules/projects/components/DescriptionEditor/resolveAppLinkTitle'
import {
    confirmOversizedUpload,
    MAX_ATTACHMENT_SIZE_MB,
} from '../../modules/projects/components/confirmOversizedUpload'

// constants
import { fileTypes, pdfTypes, videoTypes } from '../../constants/mediaTypes'

// slices
import { loginState } from '../../modules/login/login.slice'
import {
    usersListForClientModal,
    usersState,
} from '../../modules/users/users.slice'
import { getS3UrlForAttachment } from '../../modules/taskRefactored/tasks.slice'
import { uploadAttachmentToAWS } from '../../modules/taskRefactored/tasks.service'

Quill.register('modules/mention', Mention)
Quill.register('formats/mention', AvatarMentionBlot)

const MENTION_HINT_ID = '__mention_hint__'
const MENTION_LOADING_MORE_ID = '__mention_loading_more__'
const MENTION_PAGE_LIMIT = 10

const createMentionLoadingElement = (className, size) => {
    const loading = document.createElement('div')
    loading.className = className
    createRoot(loading).render(
        <Icon
            icon="line-md:loading-loop"
            style={{ fontSize: size, color: '#6366f1' }}
        />
    )
    return loading
}

const looksLikePasteUrl = (text = '') => {
    const trimmed = String(text).trim()
    if (!trimmed || /\s/.test(trimmed)) return false
    // Require an explicit URL scheme or www. — bare words like "Description"
    // must not become relative links against window.location.origin.
    if (/^https?:\/\//i.test(trimmed)) return true
    if (/^www\./i.test(trimmed)) return true
    return false
}

const deltaHasEmbed = (delta) =>
    Boolean(
        delta?.ops?.some((op) => op?.insert && typeof op.insert === 'object')
    )

const editorRootHasMedia = (quill) => {
    const root = quill?.root
    if (!root?.querySelector) return false
    const media = Array.from(
        root.querySelectorAll('img, video, iframe, .ql-video')
    ).filter(
        (el) =>
            !el.closest('.mention-editor-pill-avatar, .mention, .ql-mention')
    )
    return media.length > 0
}

/** Text and mentions always count. Inline images/video only when media comments are enabled. */
const editorHasContent = (quill, fileCount = 0, allowMedia = false) => {
    if (allowMedia && fileCount > 0) return true
    if (!quill) return false
    if (String(quill.getText() || '').trim().length > 0) return true
    const delta = quill.getContents()
    const hasMention = Boolean(delta?.ops?.some((op) => op?.insert?.mention))
    if (hasMention) return true
    if (!allowMedia) return false
    if (deltaHasEmbed(delta)) return true
    return editorRootHasMedia(quill)
}

export const commentHtmlHasMedia = (html = '') =>
    /<(img|video|iframe)\b/i.test(String(html))

const getClipboardImageFiles = (clipboardData) => {
    if (!clipboardData) return []
    const files = []
    const seen = new Set()
    const pushImage = (file) => {
        if (!file || seen.has(file)) return
        const isImage =
            String(file.type || '').startsWith('image/') ||
            /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(file.name || '')
        if (!isImage) return
        seen.add(file)
        files.push(file)
    }
    Array.from(clipboardData.items || []).forEach((item) => {
        if (
            item?.kind === 'file' &&
            String(item.type || '').startsWith('image/')
        ) {
            pushImage(item.getAsFile())
        }
    })
    Array.from(clipboardData.files || []).forEach(pushImage)
    return files
}

const getAttachmentFileName = (url = '') => {
    try {
        const raw = decodeURIComponent(
            String(url).split('?')[0].split('/').pop() || ''
        )
        return raw || 'Attachment'
    } catch {
        return 'Attachment'
    }
}

const getAttachmentKind = (url = '') => {
    const ext = `.${String(url).split('?')[0].split('.').pop()?.toLowerCase() || ''}`
    if (fileTypes.includes(ext) || ext === '.svg') return 'image'
    if (videoTypes.includes(ext)) return 'video'
    if (pdfTypes.includes(ext)) return 'pdf'
    return 'file'
}

const getAttachmentIcon = (kind) => {
    if (kind === 'video') return 'mdi:video-outline'
    if (kind === 'pdf') return 'mdi:file-pdf-box'
    return 'mdi:file-outline'
}

const MentionEditor = ({
    member = [],
    projectId,
    excludeUserIds = [],
    handleChangeMentions,
    value,
    clearTrigger,
    loadingButton,
    onClickComment,
    disabledButton,
    getFileList,
    isEditMode = false,
    onCancelEdit,
    onSaveEdit,
    saveLoading = false,
    saveDisabled = false,
    showInsertMenu = false,
    allowInlineMedia = false,
    disableMediaDrop = false,
    disableMentions = false,
    placeholder = 'Add a comment or @mention a teammate',
    mentionUserType,
}) => {
    const dispatch = useDispatch()
    const { userProfile } = useSelector(loginState)
    const { clientModalUsersListData = [] } = useSelector(usersState)
    const editorRef = useRef(null)
    const quillRef = useRef(null)
    const containerRef = useRef(null)
    const fileInputRef = useRef(null)
    const [showButton, setShowButton] = useState(false)
    const [hasBeenFocused, setHasBeenFocused] = useState(false)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const uploadedFilesRef = useRef(uploadedFiles)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState({
        current: 0,
        total: 0,
        fileName: '',
    })
    const [editorReady, setEditorReady] = useState(false)
    const [linkPopup, setLinkPopup] = useState({
        open: false,
        text: '',
        url: '',
        savedRange: null,
    })
    const linkUrlInputRef = useRef(null)
    const linkTextInputRef = useRef(null)
    const isUnmountingRef = useRef(false)
    const initialValueRef = useRef(value)

    const projectIdRef = useRef(projectId)
    const excludeUserIdsRef = useRef(excludeUserIds)
    const memberRef = useRef(member)
    const userProfileRef = useRef(userProfile)
    const mentionUsersRef = useRef([])
    const mentionHasNextRef = useRef(false)
    const mentionSearchRef = useRef('')
    const mentionFetchLoadingRef = useRef(false)
    const mentionLoadMoreRef = useRef(false)
    const mentionRequestIdRef = useRef(0)
    const mentionListScrollRef = useRef(null)
    const disableMediaDropRef = useRef(disableMediaDrop)
    const allowInlineMediaRef = useRef(allowInlineMedia)

    const mentionUserTypeRef = useRef(mentionUserType)

    useEffect(() => {
        projectIdRef.current = projectId
    }, [projectId])

    useEffect(() => {
        mentionUserTypeRef.current = mentionUserType
    }, [mentionUserType])

    useEffect(() => {
        excludeUserIdsRef.current = excludeUserIds
    }, [excludeUserIds])

    useEffect(() => {
        memberRef.current = member
    }, [member])

    useEffect(() => {
        userProfileRef.current = userProfile
    }, [userProfile])

    useEffect(() => {
        disableMediaDropRef.current = disableMediaDrop
    }, [disableMediaDrop])

    useEffect(() => {
        allowInlineMediaRef.current = allowInlineMedia
    }, [allowInlineMedia])

    const revealCommentButton = useCallback(() => {
        if (!allowInlineMediaRef.current) return
        setHasBeenFocused(true)
        setShowButton(true)
        containerRef.current?.classList.add('focused', 'expanded')
    }, [])

    const syncCommentButton = useCallback(() => {
        const has = editorHasContent(
            quillRef.current,
            uploadedFilesRef.current.length,
            allowInlineMediaRef.current
        )
        setShowButton(has)
        if (has && allowInlineMediaRef.current) revealCommentButton()
    }, [revealCommentButton])

    const syncCommentButtonRef = useRef(syncCommentButton)
    syncCommentButtonRef.current = syncCommentButton
    const revealCommentButtonRef = useRef(revealCommentButton)
    revealCommentButtonRef.current = revealCommentButton

    useEffect(() => {
        uploadedFilesRef.current = uploadedFiles
        if (allowInlineMedia) syncCommentButton()
    }, [uploadedFiles, syncCommentButton, allowInlineMedia])

    const mapApiUserToMention = (user) => ({
        id: user?._id,
        value: user?.name || user?.display || '',
        avatar: resolveProfilePictureUrl(
            user?.profilePicture || user?.image || user?.avatar
        ),
        email: user?.email,
    })

    const mapMemberToMention = (user) => ({
        id: user?.id || user?._id,
        value: user?.display || user?.name || '',
        avatar: resolveProfilePictureUrl(user?.profilePicture),
        email: user?.email,
    })

    const buildInsertMentionPayload = useCallback((datasetItem) => {
        const id = datasetItem?.id
        const fromList = mentionUsersRef.current.find(
            (u) => String(u.id) === String(id)
        )
        const avatar = resolveProfilePictureUrl(
            fromList?.avatar || datasetItem?.avatar
        )
        return {
            id: id || fromList?.id,
            value: (fromList?.value || datasetItem?.value || '').replace(
                /^@/,
                ''
            ),
            avatar,
            email: fromList?.email || datasetItem?.email || '',
            denotationChar: '',
        }
    }, [])

    const filterMentionUsers = useCallback((users) => {
        const exclude = new Set(
            [
                ...(excludeUserIdsRef.current || []),
                userProfileRef.current?._id,
            ].filter(Boolean)
        )
        return (users || []).filter((u) => u?.id && !exclude.has(u.id))
    }, [])

    const resolveMentionAvatar = useCallback(
        (id) => {
            if (!id) return ''
            const fromCache = mentionUsersRef.current.find((u) => u.id === id)
            if (fromCache?.avatar)
                return resolveProfilePictureUrl(fromCache.avatar)
            const fromModal = clientModalUsersListData.find((u) => u._id === id)
            if (fromModal?.profilePicture) {
                return resolveProfilePictureUrl(fromModal.profilePicture)
            }
            const fromMember = (memberRef.current || []).find(
                (m) => (m.id || m._id) === id
            )
            return resolveProfilePictureUrl(fromMember?.profilePicture)
        },
        [clientModalUsersListData]
    )

    const enhanceMentionNodesInEditor = useCallback(() => {
        const editor = editorRef.current?.querySelector('.ql-editor')
        if (!editor) return

        editor.querySelectorAll('span.mention').forEach((node) => {
            if (node.querySelector('.mention-editor-pill')) return

            const id = node.getAttribute('data-id')
            const rawValue =
                node.getAttribute('data-value') ||
                node.querySelector('.ql-mention-value')?.textContent ||
                ''
            const displayName = rawValue.replace(/^@/, '').trim()
            if (!displayName) return

            const avatar = resolveProfilePictureUrl(
                node.getAttribute('data-avatar') || resolveMentionAvatar(id)
            )

            node.querySelector('.ql-mention-denotation-char')?.remove()
            node.querySelector('.ql-mention-value')?.remove()
            node.innerHTML = ''
            node.contentEditable = 'false'
            node.appendChild(
                buildMentionPillElement({
                    id,
                    value: displayName,
                    avatar,
                })
            )
            AvatarMentionBlot.setDataValues(node, {
                id,
                value: displayName,
                avatar: avatar || undefined,
                denotationChar: '',
            })
        })
    }, [resolveMentionAvatar])

    const enhanceMentionNodesInEditorRef = useRef(enhanceMentionNodesInEditor)
    useEffect(() => {
        enhanceMentionNodesInEditorRef.current = enhanceMentionNodesInEditor
    }, [enhanceMentionNodesInEditor])

    const findMentionBlotAt = useCallback((quill, index) => {
        if (!quill || index == null) return null
        const [leaf] = quill.getLeaf(index)
        if (!leaf) return null
        let cur = leaf
        while (cur) {
            if (cur.statics?.blotName === 'mention') return cur
            cur = cur.parent
        }
        return null
    }, [])

    const moveCursorAfterMentionSync = useCallback(
        (quill, rangeIndex) => {
            if (!quill) return
            let idx =
                rangeIndex ??
                quill.getSelection()?.index ??
                quill.getLength() - 1

            const mentionBlot = findMentionBlotAt(quill, idx)
            if (mentionBlot) {
                idx = mentionBlot.offset(quill.scroll) + mentionBlot.length()
            }

            const nextChar = quill.getText(idx, 1)
            if (nextChar !== ' ') {
                quill.insertText(idx, ' ', 'user')
                idx += 1
            } else {
                idx += 1
            }

            quill.setSelection(idx, 0, 'user')
        },
        [findMentionBlotAt]
    )

    const placeCursorAfterMention = useCallback(() => {
        const quill = quillRef.current
        if (!quill) return
        requestAnimationFrame(() => {
            moveCursorAfterMentionSync(quill)
            quill.focus()
        })
    }, [moveCursorAfterMentionSync])

    const fetchMentionUsers = useCallback(
        async (search, skip, append) => {
            const pid = projectIdRef.current
            if (!pid) return []

            if (!append) {
                mentionFetchLoadingRef.current = true
            }

            try {
                const result = await dispatch(
                    usersListForClientModal({
                        skip,
                        limit: MENTION_PAGE_LIMIT,
                        search: search || '',
                        filter: {},
                        projectId: pid,
                        ...(mentionUserTypeRef.current
                            ? { type: mentionUserTypeRef.current }
                            : {}),
                    })
                ).unwrap()

                const mapped = filterMentionUsers(
                    (result?.data || []).map(mapApiUserToMention)
                )

                if (append) {
                    const seen = new Set(
                        mentionUsersRef.current.map((u) => u.id)
                    )
                    mentionUsersRef.current = [
                        ...mentionUsersRef.current,
                        ...mapped.filter((u) => !seen.has(u.id)),
                    ]
                } else {
                    mentionUsersRef.current = mapped
                }

                mentionHasNextRef.current = Boolean(result?.isNext)
                mentionSearchRef.current = search
                return mentionUsersRef.current
            } catch {
                if (!append) {
                    mentionUsersRef.current = []
                }
                mentionHasNextRef.current = false
                return mentionUsersRef.current
            } finally {
                if (!append) {
                    mentionFetchLoadingRef.current = false
                }
            }
        },
        [dispatch, filterMentionUsers]
    )

    const getMentionListForRender = useCallback(() => {
        const items = [...mentionUsersRef.current]
        if (mentionHasNextRef.current && mentionLoadMoreRef.current) {
            items.push({
                id: MENTION_LOADING_MORE_ID,
                value: '',
                disabled: true,
            })
        }
        return items
    }, [])

    const refreshMentionDropdown = useCallback(() => {
        const mentionMod = quillRef.current?.getModule('mention')
        if (!mentionMod || !mentionSearchRef.current) return
        mentionMod.renderList(
            '@',
            getMentionListForRender(),
            mentionSearchRef.current
        )
    }, [getMentionListForRender])

    const handleMentionListScroll = useCallback(
        async (e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target
            if (scrollHeight - scrollTop > clientHeight + 40) return
            if (!projectIdRef.current || !mentionHasNextRef.current) return
            if (mentionLoadMoreRef.current || mentionFetchLoadingRef.current) {
                return
            }

            mentionLoadMoreRef.current = true
            refreshMentionDropdown()

            const prevLen = mentionUsersRef.current.length
            try {
                await fetchMentionUsers(mentionSearchRef.current, prevLen, true)
            } finally {
                mentionLoadMoreRef.current = false
                refreshMentionDropdown()
            }
        },
        [fetchMentionUsers, refreshMentionDropdown]
    )

    const attachMentionListScroll = useCallback(() => {
        const list =
            document.querySelector(`.${assigneeStyles.assigneeSelectorList}`) ||
            document.querySelector('.ql-mention-list')

        if (!list || mentionListScrollRef.current === list) return

        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
        }

        list.addEventListener('scroll', handleMentionListScroll)
        mentionListScrollRef.current = list
    }, [handleMentionListScroll])

    const detachMentionListScroll = useCallback(() => {
        if (mentionListScrollRef.current) {
            mentionListScrollRef.current.removeEventListener(
                'scroll',
                handleMentionListScroll
            )
            mentionListScrollRef.current = null
        }
    }, [handleMentionListScroll])

    const debouncedRemoteSearchRef = useRef(
        debounce(async (searchTerm, renderList) => {
            const query = (searchTerm ?? '').trim()
            if (!query || !projectIdRef.current) return

            const requestId = ++mentionRequestIdRef.current
            await fetchMentionUsers(query, 0, false)

            if (requestId !== mentionRequestIdRef.current) return
            renderList(getMentionListForRender(), searchTerm)
        }, 300)
    )

    const renderMentionItem = useCallback((item) => {
        if (item?.id === MENTION_HINT_ID) {
            const hint = document.createElement('div')
            hint.className = 'mention-hint-item'
            hint.textContent =
                'Mention a teammate or link to a task, project, or message.'
            return hint
        }

        if (item?.id === MENTION_LOADING_MORE_ID) {
            return createMentionLoadingElement(
                assigneeStyles.assigneeSelectorLoadingMore,
                20
            )
        }

        const container = document.createElement('div')
        container.className = assigneeStyles.assigneeSelectorItem

        const avatarWrapper = document.createElement('span')
        avatarWrapper.style.cssText = [
            'width:26px',
            'height:26px',
            'min-width:26px',
            'max-width:26px',
            'min-height:26px',
            'max-height:26px',
            'border-radius:50%',
            'overflow:hidden',
            'display:inline-flex',
            'align-items:center',
            'justify-content:center',
            'flex-shrink:0',
            'font-size:14px',
            'font-weight:600',
            'color:#ffffff',
            `background-color:${getMentionAvatarColor(item?.value)}`,
        ].join(';')

        const avatarUrl = resolveProfilePictureUrl(
            item?.avatar || item?.profilePicture
        )
        if (avatarUrl) {
            const img = document.createElement('img')
            img.src = avatarUrl
            img.alt = item?.value || ''
            img.width = 26
            img.height = 26
            img.style.cssText =
                'width:26px;height:26px;min-width:26px;max-width:26px;min-height:26px;max-height:26px;object-fit:cover;display:block;'
            avatarWrapper.appendChild(img)
        } else {
            avatarWrapper.textContent = getMentionInitials(item?.value)
        }

        const info = document.createElement('div')
        info.className = assigneeStyles.assigneeSelectorItemInfo
        info.style.flexDirection = 'column'
        info.style.alignItems = 'flex-start'
        info.style.gap = '2px'

        const name = document.createElement('span')
        name.className = assigneeStyles.assigneeSelectorName
        name.textContent = item?.value || ''

        info.appendChild(name)

        if (item?.email) {
            const email = document.createElement('span')
            email.className = assigneeStyles.assigneeSelectorEmail
            email.textContent = item.email
            info.appendChild(email)
        }

        container.appendChild(avatarWrapper)
        container.appendChild(info)
        return container
    }, [])

    useEffect(() => {
        isUnmountingRef.current = false
        return () => {
            isUnmountingRef.current = true
        }
    }, [])

    useEffect(() => {
        if (!editorRef.current || quillRef.current) return
        if (!projectId && member.length === 0) return

        quillRef.current = new Quill(editorRef.current, {
            theme: 'snow',
            placeholder,
            modules: {
                toolbar: false,
                ...(disableMentions
                    ? {}
                    : {
                          mention: {
                              mentionDenotationChars: ['@'],
                              showDenotationChar: false,
                              positioningStrategy: 'fixed',
                              defaultMenuOrientation: 'bottom',
                              dataAttributes: [
                                  'id',
                                  'value',
                                  'denotationChar',
                                  'avatar',
                                  'email',
                                  'link',
                                  'target',
                                  'disabled',
                              ],
                              isolateCharacter: true,
                              spaceAfterInsert: true,
                              minChars: 0,
                              onSelect: (datasetItem, insertItem) => {
                                  if (
                                      datasetItem?.disabled ||
                                      datasetItem?.id === MENTION_HINT_ID ||
                                      datasetItem?.id ===
                                          MENTION_LOADING_MORE_ID
                                  ) {
                                      return
                                  }
                                  insertItem(
                                      buildInsertMentionPayload(datasetItem)
                                  )
                                  placeCursorAfterMention()
                                  requestAnimationFrame(() => {
                                      enhanceMentionNodesInEditorRef.current?.()
                                  })
                              },
                              mentionContainerClass: `ql-mention-list-container ${assigneeStyles.assigneeSelectorPopup}`,
                              mentionListClass: `ql-mention-list ${assigneeStyles.assigneeSelectorList}`,
                              listItemClass: 'ql-mention-list-item',
                              renderItem: renderMentionItem,
                              renderLoading: () =>
                                  createMentionLoadingElement(
                                      assigneeStyles.assigneeSelectorLoading,
                                      24
                                  ),
                              onOpen: () => {
                                  setTimeout(() => attachMentionListScroll(), 0)
                              },
                              onBeforeClose: () => {
                                  detachMentionListScroll()
                              },
                              source: (searchTerm, renderList) => {
                                  const query = (searchTerm ?? '').trim()
                                  if (!query) {
                                      renderList(
                                          [
                                              {
                                                  id: MENTION_HINT_ID,
                                                  value: '',
                                                  disabled: true,
                                              },
                                          ],
                                          searchTerm
                                      )
                                      return
                                  }

                                  if (projectIdRef.current) {
                                      mentionRequestIdRef.current += 1
                                      debouncedRemoteSearchRef.current.cancel()
                                      debouncedRemoteSearchRef.current(
                                          searchTerm,
                                          renderList
                                      )
                                      return
                                  }

                                  const q = query.toLowerCase()
                                  const matches = filterMentionUsers(
                                      (memberRef.current || [])
                                          .map(mapMemberToMention)
                                          .filter((item) => {
                                              const name = (
                                                  item.value || ''
                                              ).toLowerCase()
                                              const email = (
                                                  item.email || ''
                                              ).toLowerCase()
                                              return (
                                                  name.includes(q) ||
                                                  email.includes(q)
                                              )
                                          })
                                  )
                                  renderList(matches, searchTerm)
                              },
                          },
                      }),
            },
        })

        // Set placeholder data attribute for CSS
        const editorElement = editorRef.current.querySelector('.ql-editor')
        if (editorElement) {
            editorElement.setAttribute('data-placeholder', placeholder)
        }

        const initialHtml = initialValueRef.current
        if (initialHtml && typeof initialHtml === 'string') {
            quillRef.current.clipboard.dangerouslyPasteHTML(initialHtml)
            requestAnimationFrame(() => enhanceMentionNodesInEditor())
        }

        quillRef.current.on('selection-change', (range) => {
            if (!range || range.length > 0) return
            const quill = quillRef.current
            if (!quill || !findMentionBlotAt(quill, range.index)) return
            moveCursorAfterMentionSync(quill, range.index)
        })

        quillRef.current.on('text-change', () => {
            enhanceMentionNodesInEditorRef.current?.()

            const html =
                editorRef.current.querySelector('.ql-editor')?.innerHTML || ''
            const delta = quillRef.current.getContents()
            const mentions = []
            let plainText = ''

            delta.ops?.forEach((op) => {
                if (typeof op.insert === 'string') {
                    plainText += op.insert
                } else if (op.insert?.mention) {
                    mentions.push(op.insert.mention.id)
                    const name = (op.insert.mention.value || '').replace(
                        /^@/,
                        ''
                    )
                    plainText += name ? `${name} ` : ''
                }
            })

            handleChangeMentions(html, plainText.trim(), [...new Set(mentions)])

            const allowMedia = allowInlineMediaRef.current
            const has = editorHasContent(
                quillRef.current,
                uploadedFilesRef.current.length,
                allowMedia
            )
            setShowButton(has)
            if (has && allowMedia) {
                setHasBeenFocused(true)
                containerRef.current?.classList.add('focused', 'expanded')
            }
        })

        setEditorReady(true)
    }, [
        member,
        projectId,
        disableMentions,
        handleChangeMentions,
        renderMentionItem,
        attachMentionListScroll,
        detachMentionListScroll,
        filterMentionUsers,
        enhanceMentionNodesInEditor,
        placeCursorAfterMention,
        findMentionBlotAt,
        moveCursorAfterMentionSync,
        buildInsertMentionPayload,
        placeholder,
    ])

    // Keep mention menu portaled even if Quill was created before this option existed.
    useEffect(() => {
        if (!editorReady) return
        const mention = quillRef.current?.getModule('mention')
        if (!mention?.options) return
        mention.options.positioningStrategy = 'fixed'
        mention.options.defaultMenuOrientation = 'bottom'
    }, [editorReady])

    // The mention list is portaled to document.body, so it never sees clicks
    // outside the editor or the drawer closing.
    useEffect(() => {
        if (!editorReady) return
        const quill = quillRef.current
        if (!quill?.root) return

        const closeMentionList = () => {
            const mentionMod = quill.getModule('mention')
            if (!mentionMod) return
            mentionRequestIdRef.current += 1
            debouncedRemoteSearchRef.current?.cancel()
            if (mentionMod.existingSourceExecutionToken) {
                mentionMod.existingSourceExecutionToken.abandoned = true
                mentionMod.existingSourceExecutionToken = undefined
            }
            if (mentionMod.isOpen || mentionMod.mentionContainer?.isConnected) {
                mentionMod.hideMentionList()
            }
        }

        const handleOutsidePointer = (e) => {
            const container = quill.getModule('mention')?.mentionContainer
            if (!container?.isConnected) return
            const target = e.target
            if (quill.root.contains(target) || container.contains(target)) {
                return
            }
            closeMentionList()
        }

        document.addEventListener('mousedown', handleOutsidePointer, true)
        return () => {
            document.removeEventListener('mousedown', handleOutsidePointer, true)
            closeMentionList()
        }
    }, [editorReady])

    // Paste / link-click / @-after-mention: keep listeners attached even when
    // parent re-renders and updates `value` (init effect early-returns).
    useEffect(() => {
        if (!editorReady) return
        const quill = quillRef.current
        if (!quill?.root) return

        const editorElement = quill.root

        const handleEditorFocus = () => {
            setHasBeenFocused(true)
            containerRef.current?.classList.add('focused')
        }

        const handlePaste = (e) => {
            const allowMedia =
                allowInlineMediaRef.current && !disableMediaDropRef.current
            const imageFiles = allowMedia
                ? getClipboardImageFiles(e.clipboardData)
                : []
            const plain = e.clipboardData?.getData('text/plain') || ''
            const clipboardHtml = e.clipboardData?.getData('text/html') || ''

            // Task comments only: screenshot / copy-image has a file and no text.
            if (imageFiles.length > 0 && !String(plain).trim()) {
                e.preventDefault()
                e.stopPropagation()
                const editor = quillRef.current
                if (!editor) return
                revealCommentButtonRef.current?.()

                const range = editor.getSelection(true) ?? {
                    index: Math.max(editor.getLength() - 1, 0),
                    length: 0,
                }
                if (range.length) {
                    editor.deleteText(range.index, range.length, 'user')
                }

                imageFiles.forEach((file) => {
                    const reader = new FileReader()
                    reader.onload = (ev) => {
                        const q = quillRef.current
                        if (!q || isUnmountingRef.current) return
                        const insertAt = Math.max(q.getLength() - 1, 0)
                        q.insertEmbed(
                            insertAt,
                            'image',
                            ev.target.result,
                            'user'
                        )
                        q.setSelection(insertAt + 1, 0, 'user')
                        revealCommentButtonRef.current?.()
                    }
                    reader.readAsDataURL(file)
                })
                return
            }

            if (!looksLikePasteUrl(plain)) {
                if (allowMedia && /<img\b/i.test(clipboardHtml)) {
                    revealCommentButtonRef.current?.()
                    window.setTimeout(() => syncCommentButtonRef.current?.(), 0)
                    window.setTimeout(
                        () => syncCommentButtonRef.current?.(),
                        50
                    )
                }
                return
            }

            e.preventDefault()
            e.stopPropagation()

            const editor = quillRef.current
            if (!editor) return

            const pasted = plain.trim()
            const urlCandidate = /^www\./i.test(pasted)
                ? `https://${pasted}`
                : pasted
            let href
            try {
                href = new URL(urlCandidate).href
            } catch {
                href = urlCandidate
            }

            const range = editor.getSelection(true) ?? {
                index: Math.max(editor.getLength() - 1, 0),
                length: 0,
            }
            if (range.length) {
                editor.deleteText(range.index, range.length, 'user')
            }

            const insertIndex = range.index
            editor.insertText(insertIndex, pasted, 'link', href, 'user')
            editor.setSelection(insertIndex + pasted.length, 0, 'silent')

            resolveAppLinkTitle(pasted).then((resolved) => {
                if (isUnmountingRef.current || !resolved?.title) return
                const q = quillRef.current
                if (!q || q !== editor) return
                const current = q.getText(insertIndex, pasted.length)
                if (current !== pasted) return
                const resolvedHref = resolved.href || href
                q.deleteText(insertIndex, pasted.length, 'user')
                q.insertText(
                    insertIndex,
                    resolved.title,
                    'link',
                    resolvedHref,
                    'user'
                )
                q.setSelection(insertIndex + resolved.title.length, 0, 'silent')
            })
        }

        const handleLinkClick = (e) => {
            const anchor = e.target?.closest?.('a[href]')
            if (!anchor || !editorElement.contains(anchor)) return
            const href = anchor.getAttribute('href')
            if (!href || href === '#') return
            e.preventDefault()
            e.stopPropagation()
            window.open(href, '_blank', 'noopener,noreferrer')
        }

        const handleEditorKeyDown = (e) => {
            if (e.key !== '@') return
            const editor = quillRef.current
            if (!editor) return
            const range = editor.getSelection()
            if (range == null) return
            const mentionBlot = findMentionBlotAt(editor, range.index)
            if (!mentionBlot) return

            e.preventDefault()
            let idx = mentionBlot.offset(editor.scroll) + mentionBlot.length()
            const nextChar = editor.getText(idx, 1)
            if (nextChar !== ' ') {
                editor.insertText(idx, ' ', 'user')
                idx += 1
            } else {
                idx += 1
            }
            editor.insertText(idx, '@', 'user')
            editor.setSelection(idx + 1, 0, 'user')
        }

        editorElement.addEventListener('paste', handlePaste, true)
        editorElement.addEventListener('click', handleLinkClick)
        editorElement.addEventListener('keydown', handleEditorKeyDown)
        if (allowInlineMediaRef.current) {
            editorElement.addEventListener('focus', handleEditorFocus)
        }

        let mediaObserver = null
        if (allowInlineMediaRef.current) {
            mediaObserver = new MutationObserver(() => {
                syncCommentButtonRef.current?.()
            })
            mediaObserver.observe(editorElement, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['src'],
            })
        }

        return () => {
            mediaObserver?.disconnect()
            editorElement.removeEventListener('paste', handlePaste, true)
            editorElement.removeEventListener('click', handleLinkClick)
            editorElement.removeEventListener('keydown', handleEditorKeyDown)
            editorElement.removeEventListener('focus', handleEditorFocus)
        }
    }, [editorReady, findMentionBlotAt])

    // Story map comments: block image/file drag-drop and image paste into the editor.
    useEffect(() => {
        if (!editorReady || !disableMediaDrop) return
        const quill = quillRef.current
        if (!quill?.root) return

        const editorElement = quill.root

        const hasFiles = (dataTransfer) => {
            if (!dataTransfer) return false
            if (dataTransfer.files?.length > 0) return true
            const types = Array.from(dataTransfer.types || [])
            return types.includes('Files')
        }

        const blockDrop = (e) => {
            if (!hasFiles(e.dataTransfer)) return
            e.preventDefault()
            e.stopPropagation()
        }

        const blockPasteFiles = (e) => {
            const items = Array.from(e.clipboardData?.items || [])
            const hasMedia = items.some(
                (item) =>
                    item.kind === 'file' ||
                    String(item.type || '').startsWith('image/')
            )
            if (!hasMedia) return
            e.preventDefault()
            e.stopPropagation()
        }

        quill.clipboard.addMatcher('IMG', (node, delta) => {
            delta.ops = []
            return delta
        })

        editorElement.addEventListener('dragenter', blockDrop, true)
        editorElement.addEventListener('dragover', blockDrop, true)
        editorElement.addEventListener('drop', blockDrop, true)
        editorElement.addEventListener('paste', blockPasteFiles, true)

        const removeEmbeddedImages = () => {
            const editor = quillRef.current
            if (!editor) return
            const imgs = Array.from(editor.root.querySelectorAll('img')).filter(
                (img) =>
                    !img.closest(
                        '.mention-editor-pill-avatar, .mention, .ql-mention'
                    )
            )
            if (!imgs.length) return
            imgs.forEach((img) => img.remove())
        }

        quill.on('text-change', removeEmbeddedImages)

        return () => {
            editorElement.removeEventListener('dragenter', blockDrop, true)
            editorElement.removeEventListener('dragover', blockDrop, true)
            editorElement.removeEventListener('drop', blockDrop, true)
            editorElement.removeEventListener('paste', blockPasteFiles, true)
            quill.off('text-change', removeEmbeddedImages)
        }
    }, [editorReady, disableMediaDrop])

    useEffect(() => {
        const debouncedSearch = debouncedRemoteSearchRef.current
        return () => {
            debouncedSearch?.cancel()
            detachMentionListScroll()
        }
    }, [detachMentionListScroll])

    // external clear trigger
    useEffect(() => {
        if (clearTrigger && quillRef.current) {
            quillRef.current.setText('')
            setShowButton(false)
            // Clear uploaded files when editor is cleared
            setUploadedFiles([])
            if (getFileList) {
                getFileList([])
            }
        }
    }, [clearTrigger, getFileList])

    const handleFocus = () => {
        containerRef.current?.classList.add('focused')
        setHasBeenFocused(true)
    }
    const handleBlur = () => {
        containerRef.current?.classList.remove('focused')
        // Keep the editor expanded if it has been focused before
        if (hasBeenFocused) {
            containerRef.current?.classList.add('expanded')
        }
    }

    // 👇 Clear editor on submit
    const handleSubmit = () => {
        onClickComment()
        if (quillRef.current) {
            quillRef.current.setText('') // clear text
            setShowButton(false) // hide button again
        }
    }

    // Handle file selection
    const handleFileSelect = () => {
        fileInputRef.current?.click()
    }

    // Handle file upload via support/upload-file (same as Add Files)
    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files || [])
        if (files.length === 0) return

        const acceptedExts = [...fileTypes, ...videoTypes, ...pdfTypes]
        const oversizedNames = []
        const validFiles = []

        files.forEach((file) => {
            const ext = `.${String(file.name || '')
                .split('.')
                .pop()
                ?.toLowerCase()}`
            if (!acceptedExts.includes(ext)) {
                message.error(`Unsupported file type: ${file.name || 'file'}`)
                return
            }
            if (file.size / 1024 / 1024 > MAX_ATTACHMENT_SIZE_MB) {
                if (file?.name) oversizedNames.push(file.name)
                return
            }
            validFiles.push(file)
        })

        if (oversizedNames.length > 0) {
            confirmOversizedUpload({
                fileNames: oversizedNames,
                uploadingCount: files.length,
                maxSizeMb: MAX_ATTACHMENT_SIZE_MB,
            })
        }

        if (validFiles.length === 0) {
            if (fileInputRef.current) fileInputRef.current.value = ''
            return
        }

        setUploading(true)
        setUploadProgress({
            current: 0,
            total: validFiles.length,
            fileName: validFiles[0]?.name || '',
        })
        const newFileUrls = []

        try {
            for (let i = 0; i < validFiles.length; i += 1) {
                const file = validFiles[i]
                setUploadProgress({
                    current: i + 1,
                    total: validFiles.length,
                    fileName: file.name || `File ${i + 1}`,
                })

                const nameChange = file.name
                    .split('.')[0]
                    .concat(`_${dayjs(new Date()).unix()}`)
                    .concat(`.${file.name.split('.').pop()}`)

                const formData = {
                    data: [
                        {
                            filename: nameChange,
                            contentType: file.type,
                        },
                    ],
                }

                const result = await dispatch(getS3UrlForAttachment(formData))
                if (!result?.payload?.data?.success) {
                    message.error('Upload Failed! Please try again')
                    continue
                }

                const response = result?.payload?.data?.data
                const uploadUrl = response?.[0]?.uploadUrl
                const publicLink = response?.[0]?.publicLink

                if (!uploadUrl || !publicLink) {
                    message.error('Upload Failed! Please try again')
                    continue
                }

                await uploadAttachmentToAWS({
                    url: uploadUrl,
                    data: file,
                })
                newFileUrls.push(publicLink)
            }

            if (newFileUrls.length > 0) {
                setUploadedFiles((prev) => {
                    const updated = [...prev, ...newFileUrls]
                    if (getFileList) {
                        getFileList(updated)
                    }
                    return updated
                })
                setShowButton(true)
            }
        } catch (error) {
            console.error('Upload error:', error)
            message.error(error?.message || 'Upload Failed! Please try again')
        } finally {
            setUploading(false)
            setUploadProgress({ current: 0, total: 0, fileName: '' })
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    // Handle file delete
    const handleFileDelete = (fileUrl) => {
        const updatedFiles = uploadedFiles.filter((url) => url !== fileUrl)
        setUploadedFiles(updatedFiles)

        // Notify parent component
        if (getFileList) {
            getFileList(updatedFiles)
        }
        setShowButton(editorHasContent(quillRef.current, updatedFiles.length))
    }

    // Formatting handlers
    const handleFormat = (format) => {
        if (quillRef.current) {
            const range = quillRef.current.getSelection(true)
            if (range) {
                const formatValue = quillRef.current.getFormat(range)
                const isActive = formatValue[format]
                quillRef.current.format(format, !isActive)
            }
        }
    }

    const handleBold = () => handleFormat('bold')
    const handleItalic = () => handleFormat('italic')
    const handleUnderline = () => handleFormat('underline')

    const closeLinkPopup = useCallback(() => {
        setLinkPopup({ open: false, text: '', url: '', savedRange: null })
        quillRef.current?.focus()
    }, [])

    const openLinkPopup = useCallback(() => {
        const q = quillRef.current
        if (!q) return
        q.focus()
        const range = q.getSelection(true) ?? {
            index: Math.max(q.getLength() - 1, 0),
            length: 0,
        }
        const fmt = range.length ? q.getFormat(range) : q.getFormat(range.index)
        const selectedText = range.length
            ? q.getText(range.index, range.length)
            : ''
        setLinkPopup({
            open: true,
            text: selectedText,
            url: getLinkHref(fmt?.link) || '',
            savedRange: range,
        })
    }, [])

    const handleLinkSubmit = useCallback(() => {
        const q = quillRef.current
        if (!q) return
        const { text, url, savedRange } = linkPopup
        if (!url.trim()) {
            if (savedRange?.length) {
                q.setSelection(savedRange.index, savedRange.length, 'silent')
                q.format('link', false)
            }
            closeLinkPopup()
            return
        }
        const href = url.trim()
        if (savedRange?.length) {
            q.setSelection(savedRange.index, savedRange.length, 'silent')
            q.format('link', href)
        } else {
            const insertText = text.trim() || href
            const idx = savedRange?.index ?? Math.max(q.getLength() - 1, 0)
            q.insertText(idx, insertText, 'link', href, 'user')
            q.setSelection(idx + insertText.length, 0)
        }
        closeLinkPopup()
    }, [linkPopup, closeLinkPopup])

    const handleLinkKeyDown = useCallback(
        (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleLinkSubmit()
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                closeLinkPopup()
            }
        },
        [handleLinkSubmit, closeLinkPopup]
    )

    useEffect(() => {
        if (!linkPopup.open) return
        const focusTarget = linkPopup.text
            ? linkUrlInputRef.current
            : linkTextInputRef.current
        const timer = setTimeout(() => focusTarget?.focus(), 0)
        return () => clearTimeout(timer)
    }, [linkPopup.open]) // eslint-disable-line react-hooks/exhaustive-deps

    const linkPopoverContent = (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <input
                ref={linkTextInputRef}
                className={insertMenuStyles.linkInput}
                value={linkPopup.text}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, text: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder="Text"
                type="text"
            />
            <input
                ref={linkUrlInputRef}
                className={insertMenuStyles.linkInput}
                value={linkPopup.url}
                onChange={(e) =>
                    setLinkPopup((s) => ({ ...s, url: e.target.value }))
                }
                onKeyDown={handleLinkKeyDown}
                placeholder="URL"
                type="url"
            />
            <div className={insertMenuStyles.linkActions}>
                <button
                    type="button"
                    className={insertMenuStyles.linkCancelBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        closeLinkPopup()
                    }}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={insertMenuStyles.linkSubmitBtn}
                    onMouseDown={(e) => {
                        e.preventDefault()
                        handleLinkSubmit()
                    }}
                >
                    Apply
                </button>
            </div>
        </div>
    )

    return (
        <>
            <div
                ref={containerRef}
                className={[
                    'mention-editor-container',
                    allowInlineMedia ? 'allow-inline-media' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <div
                    ref={editorRef}
                    className="mention-editor"
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />

                {getFileList && uploadedFiles.length > 0 ? (
                    <div className="mention-editor-attachments">
                        {uploadedFiles.map((fileUrl) => {
                            const kind = getAttachmentKind(fileUrl)
                            const fileName = getAttachmentFileName(fileUrl)
                            const isImage = kind === 'image'
                            const typeLabel =
                                kind === 'image'
                                    ? 'Image'
                                    : kind === 'video'
                                      ? 'Video'
                                      : kind === 'pdf'
                                        ? 'PDF'
                                        : 'File'

                            return (
                                <div
                                    key={fileUrl}
                                    className="mention-editor-attachmentCard"
                                    title={fileName}
                                >
                                    <div className="mention-editor-attachmentPreview">
                                        {isImage ? (
                                            <img
                                                src={fileUrl}
                                                alt={fileName}
                                                className="mention-editor-attachmentThumb"
                                            />
                                        ) : (
                                            <span
                                                className={`mention-editor-attachmentIcon mention-editor-attachmentIcon-${kind}`}
                                            >
                                                <Icon
                                                    icon={getAttachmentIcon(
                                                        kind
                                                    )}
                                                    width={20}
                                                    height={20}
                                                    aria-hidden
                                                />
                                            </span>
                                        )}
                                    </div>
                                    <div className="mention-editor-attachmentMeta">
                                        <span className="mention-editor-attachmentName">
                                            {fileName}
                                        </span>
                                        <span className="mention-editor-attachmentType">
                                            {typeLabel}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="mention-editor-attachmentRemove"
                                        aria-label={`Remove ${fileName}`}
                                        onClick={() =>
                                            handleFileDelete(fileUrl)
                                        }
                                    >
                                        <Icon
                                            icon="mdi:close"
                                            width={14}
                                            height={14}
                                            aria-hidden
                                        />
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                ) : null}

                {getFileList && uploading ? (
                    <div className="mention-editor-uploading">
                        <div className="mention-editor-uploadingCard">
                            <span
                                className="mention-editor-uploadingSpinner"
                                aria-hidden
                            >
                                <Icon
                                    icon="line-md:uploading-loop"
                                    width={20}
                                    height={20}
                                />
                            </span>
                            <div className="mention-editor-uploadingMeta">
                                <span className="mention-editor-uploadingTitle">
                                    {uploadProgress.total > 1
                                        ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}`
                                        : 'Uploading file'}
                                </span>
                                <span className="mention-editor-uploadingName">
                                    {uploadProgress.fileName ||
                                        'Preparing upload…'}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}

                {(hasBeenFocused || showButton) && (
                    <div className="mention-editor-actions">
                        {hasBeenFocused && (
                            <div className="mention-editor-formatting">
                                {showInsertMenu && (
                                    <MentionEditorInsertMenu
                                        quillRef={quillRef}
                                    />
                                )}
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="octicon:bold-16"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleBold,
                                        title: 'Bold',
                                        'aria-label': 'Bold',
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="si:italic-fill"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleItalic,
                                        title: 'Italic',
                                        'aria-label': 'Italic',
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Button
                                    props={{
                                        icon: (
                                            <Icon
                                                icon="solar:text-underline-linear"
                                                width={16}
                                                height={16}
                                            />
                                        ),
                                        buttonType: 'link',
                                        onClick: handleUnderline,
                                        title: 'Underline',
                                        'aria-label': 'Underline',
                                        style: {
                                            color: '#64748b',
                                            padding: 0,
                                        },
                                    }}
                                />
                                <Popover
                                    open={linkPopup.open}
                                    onOpenChange={(open) => {
                                        if (!open) closeLinkPopup()
                                    }}
                                    trigger={[]}
                                    placement="topLeft"
                                    overlayClassName={
                                        insertMenuStyles.linkPopover
                                    }
                                    getPopupContainer={() => document.body}
                                    content={linkPopoverContent}
                                    destroyOnHidden
                                >
                                    <span>
                                        <Button
                                            props={{
                                                icon: (
                                                    <Icon
                                                        icon="ri:link"
                                                        width={16}
                                                        height={16}
                                                    />
                                                ),
                                                buttonType: 'link',
                                                onClick: openLinkPopup,
                                                onMouseDown: (e) =>
                                                    e.preventDefault(),
                                                title: 'Insert link',
                                                'aria-label': 'Insert link',
                                                style: {
                                                    color: '#64748b',
                                                    padding: 0,
                                                },
                                            }}
                                        />
                                    </span>
                                </Popover>
                                {getFileList && (
                                    <Button
                                        props={{
                                            icon: (
                                                <Icon
                                                    icon="ic:baseline-attach-file"
                                                    width={16}
                                                    height={16}
                                                />
                                            ),
                                            buttonType: 'link',
                                            type: 'files',
                                            onClick: handleFileSelect,
                                            title: 'Attach file',
                                            'aria-label': 'Attach file',
                                            style: {
                                                letterSpacing: '0.7px',
                                                color: '#64748b',
                                                padding: 0,
                                            },
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        <div className="mention-editor-right-actions">
                            {isEditMode ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '8px',
                                    }}
                                >
                                    <Button
                                        props={{
                                            text: 'Cancel',
                                            onClick: onCancelEdit,
                                            style: {
                                                height: '36px',
                                                padding: '0 14px',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                color: '#475569',
                                            },
                                        }}
                                    />
                                    <Button
                                        props={{
                                            text: 'Save changes',
                                            onClick: onSaveEdit,
                                            loadingButton: saveLoading,
                                            disabled: saveDisabled,
                                            style: {
                                                height: '36px',
                                                padding: '0 14px',
                                                borderRadius: '8px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                backgroundColor: '#4F46E5',
                                                border: 'none',
                                                color: '#fff',
                                            },
                                        }}
                                    />
                                </div>
                            ) : (
                                showButton && (
                                    <div className="comment-button-wrapper">
                                        <Button
                                            props={{
                                                text: 'Comment',
                                                style: {
                                                    margin: 0,
                                                    height: '36px',
                                                    padding: '0 14px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#4F46E5',
                                                    border: 'none',
                                                    color: '#fff',
                                                    fontWeight: 400,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 14,
                                                },
                                                loadingButton: loadingButton,
                                                onClick: handleSubmit,
                                                disabled: allowInlineMedia
                                                    ? disabledButton &&
                                                      !showButton
                                                    : disabledButton,
                                            }}
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>

            {getFileList ? (
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept={[...fileTypes, ...videoTypes, ...pdfTypes].join(
                        ','
                    )}
                />
            ) : null}
        </>
    )
}

export default MentionEditor
